import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ImageOff, ImageIcon, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import catalog from "@/api/catalog";

/**
 * Phase 5.6 — Dashboard widget that surfaces SKUs without a photo.
 *
 * Reads `with_photo_count` / `without_photo_count` from /api/catalog/kpis.
 * Renders a clickable card that links to `/catalog?photos=missing`. Uses an
 * amber tone (operational, not urgent). Hides itself if every product has a
 * photo (rare).
 *
 * Failure states:
 *   - `loading`: skeleton.
 *   - `error`:  shows dash "—" so the rest of the dashboard isn't broken.
 */
export default function PhotoMissingWidget() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let alive = true;
    catalog
      .kpis()
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        setState("ok");
      })
      .catch(() => {
        if (alive) setState("error");
      });
    return () => {
      alive = false;
    };
  }, []);

  if (state === "loading") {
    return (
      <Card data-testid="photo-missing-widget-loading">
        <CardContent className="p-5">
          <div className="h-3 w-24 animate-pulse rounded bg-muted" />
          <div className="mt-2 h-8 w-32 animate-pulse rounded bg-muted" />
          <div className="mt-1 h-3 w-40 animate-pulse rounded bg-muted" />
        </CardContent>
      </Card>
    );
  }

  if (state === "error" || !data) {
    return (
      <Card data-testid="photo-missing-widget-error">
        <CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            Productos sin foto
          </div>
          <div className="mt-1 text-3xl font-semibold tabular-nums text-muted-foreground">
            —
          </div>
        </CardContent>
      </Card>
    );
  }

  const total = (data.with_photo_count || 0) + (data.without_photo_count || 0);
  const without = data.without_photo_count || 0;
  const pct = total > 0 ? (without / total) * 100 : 0;

  // Happy path — everything has a photo. Show a tiny success state.
  if (without === 0 && total > 0) {
    return (
      <Card
        data-testid="photo-missing-widget-zero"
        className="border-emerald-500/30 bg-emerald-500/5"
      >
        <CardContent className="p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
            <ImageIcon className="h-3 w-3" />
            Cobertura de fotos
          </div>
          <div className="mt-1 text-3xl font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
            100%
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            Todos los productos tienen foto
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Link
      to="/catalog?photos=missing"
      data-testid="photo-missing-widget"
      className="block focus:outline-none"
    >
      <Card
        className={cn(
          "group cursor-pointer overflow-hidden border-amber-500/30 bg-amber-500/5",
          "transition-all hover:-translate-y-0.5 hover:border-amber-500/60 hover:shadow-lg"
        )}
      >
        <CardContent className="relative p-5">
          <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-amber-500/10" />
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-600 dark:text-amber-400">
                <ImageOff className="h-3 w-3" />
                Productos sin foto
              </div>
              <div
                className="mt-1 text-3xl font-semibold tabular-nums"
                data-testid="photo-missing-widget-count"
              >
                {without.toLocaleString("es-MX")}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {without.toLocaleString("es-MX")} SKUs · {pct.toFixed(1)}% del catálogo
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 transition-colors group-hover:text-amber-500 dark:text-amber-400">
                Revisar catálogo
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </div>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <ImageOff className="h-5 w-5" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
